import React, {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiCopy, FiFlag, FiPlus, FiStar, FiX, FiChevronRight, FiSquare } from "react-icons/fi";
import useToast from "hooks/useToast";
import {
  clearChatHistory,
  fetchChatHistory,
  startNewChat,
  streamChatReply,
  submitChatFeedback,
  submitMessageDislike,
  submitMessageLike,
  submitMessageReport,
} from "./chatBotApi";
import varunImg from "./varun.webp";

const MESSAGE_ROLES = {
  USER: "user",
  ASSISTANT: "assistant",
};

const EXAM_TYPES = {
  ACADEMIC: "academic",
  CORE: "core",
};

const EXAM_CONFIG = {
  [EXAM_TYPES.ACADEMIC]: {
    name: "PTE Academic",
    ctx: "for study & migration",
  },
  [EXAM_TYPES.CORE]: {
    name: "PTE Core",
    ctx: "for work & PR in Canada and Australia",
  },
};

const STARTER_QUESTIONS = [
  "Why am I stuck at 65 in Speaking?",
  "How does the algorithm mark my essay?",
  "What should I practise in my last 12 days?",
];

const NOVA_STAR_PATH =
  "M12 1.6c.55 5 2.85 7.3 7.85 7.85-5 .55-7.3 2.85-7.85 7.85-.55-5-2.85-7.3-7.85-7.85 5-.55 7.3-2.85 7.85-7.85Z";

const REPORT_MIN_LENGTH = 10;
const SUBSCRIPTION_ROUTE = "/admin/plans";
const DEFAULT_ERROR_MESSAGE =
  "Nova couldn't reach the coach just now. Your credit wasn't used — try again.";
const CORE_NOVA_ACTIVE =
  String(import.meta.env.VITE_CORE_NOVA_ACTIVE).toLowerCase() === "true";

// --- Utility functions (unchanged logic) ---

function createMessageId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function esc(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inlineMarkdown(text) {
  return esc(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

function formatChatMessage(source) {
  const text = String(source).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!text) return "";

  const lines = text.split("\n");
  let out = "";
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (line === "") { i += 1; continue; }

    if (/^#{1,6}\s/.test(line)) {
      const headingText = line.replace(/^#{1,6}\s+/, "");
      out += `<h3 class="nova-md-heading"><strong>${inlineMarkdown(headingText)}</strong></h3>`;
      i += 1;
      continue;
    }

    if (/^\d+\.\s/.test(line)) {
      out += "<ol>";
      while (i < lines.length) {
        const item = lines[i].trim();
        if (!item || !/^\d+\.\s/.test(item)) break;
        out += `<li>${inlineMarkdown(item.replace(/^\d+\.\s+/, ""))}</li>`;
        i += 1;
      }
      out += "</ol>";
      continue;
    }

    if (/^-\s/.test(line)) {
      out += "<ul>";
      while (i < lines.length) {
        const item = lines[i].trim();
        if (!item || !/^-\s/.test(item)) break;
        out += `<li>${inlineMarkdown(item.replace(/^-\s+/, ""))}</li>`;
        i += 1;
      }
      out += "</ul>";
      continue;
    }

    out += `<p>${inlineMarkdown(line)}</p>`;
    i += 1;
  }

  return out || `<p>${inlineMarkdown(text)}</p>`;
}

function markdownToPlainText(source) {
  const text = String(source).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!text) return "";
  const lines = text.split("\n");
  const out = [];
  for (const line of lines) {
    if (line.trim() === "") { out.push(""); continue; }
    const trimmed = line.trim();
    if (/^#{1,6}\s/.test(trimmed)) { out.push(trimmed.replace(/^#{1,6}\s+/, "")); continue; }
    if (/^>\s?/.test(trimmed)) { out.push(trimmed.replace(/^>\s?/, "")); continue; }
    if (/^\d+\.\s/.test(trimmed)) { out.push(trimmed.replace(/^\d+\.\s+/, "")); continue; }
    if (/^[-*+]\s/.test(trimmed)) { out.push(trimmed.replace(/^[-*+]\s+/, "")); continue; }
    out.push(trimmed);
  }
  return out.join("\n")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/<[^>]+>/g, "");
}

function formatChatMessageWithCaret(source) {
  return `${formatChatMessage(source)}<span class="nova-caret"></span>`;
}

async function copyToClipboard(text) {
  const value = String(text || "");
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch {
    document.body.removeChild(textarea);
    return false;
  }
}

// --- Small UI Components ---

function NovaMark({ size = "md" }) {
  const cls = size === "lg" ? "nova-mark lg" : size === "sm" ? "nova-mark sm" : "nova-mark";
  return (
    <div className={cls} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d={NOVA_STAR_PATH} />
      </svg>
    </div>
  );
}

function ThinkingState() {
  return (
    <div className="nova-thinking" aria-label="Nova is thinking">
      {/* <NovaMark size="sm" /> */}
      <span className="nova-thinking-text">Checking Varun's method…</span>
    </div>
  );
}

function ReportPopover({ threadId, messageIndex, onClose }) {
  const { showToast } = useToast();
  const popoverRef = useRef(null);
  const textareaRef = useRef(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { textareaRef.current?.focus(); }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) onClose();
    };
    const handleKeyDown = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const trimmed = reason.trim();
    if (trimmed.length < REPORT_MIN_LENGTH) {
      setError(`Please enter at least ${REPORT_MIN_LENGTH} characters.`);
      return;
    }
    if (!threadId) {
      showToast({ type: 2, message: "Unable to report this message. Please try again later." });
      return;
    }
    setError("");
    setIsSubmitting(true);
    try {
      await submitMessageReport({ threadId, messageIndex, comment: trimmed });
      showToast({ type: 1, message: "Report submitted. Thank you for your feedback." });
      onClose();
    } catch (submitError) {
      showToast({ type: 2, message: submitError?.message || "Failed to submit report." });
    } finally {
      setIsSubmitting(false);
    }
  }, [messageIndex, onClose, reason, showToast, threadId]);

  return (
    <div className="nova-report-popover" ref={popoverRef} role="dialog" aria-label="Report message">
      <p className="nova-report-title">Report this response</p>
      <textarea
        ref={textareaRef}
        rows={3}
        value={reason}
        placeholder="Describe the issue with this response..."
        onChange={(event) => { setReason(event.target.value); if (error) setError(""); }}
        disabled={isSubmitting}
      />
      {error && <p className="nova-report-error">{error}</p>}
      <div className="nova-report-actions">
        <button type="button" className="cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button>
        <button type="button" className="submit" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit report"}
        </button>
      </div>
    </div>
  );
}

function MessageActions({ message, threadId, messageIndex, onFeedback }) {
  const { showToast } = useToast();
  const [reportOpen, setReportOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(markdownToPlainText(message.content));
    if (success) {
      setCopied(true);
      showToast({ type: 1, message: "Copied" });
      setTimeout(() => setCopied(false), 2000);
    }
  }, [message.content, showToast]);

  return (
    <div className="nova-acts">
      <div className="nova-report-wrap">
        <button
          type="button"
          className={`report ${reportOpen ? "act-on" : ""}`}
          title="Report"
          aria-label="Report response"
          aria-expanded={reportOpen}
          onClick={() => setReportOpen((open) => !open)}
        >
          <FiFlag size={14} />
          <span>Report</span>
        </button>
        {reportOpen && (
          <ReportPopover
            threadId={threadId}
            messageIndex={messageIndex}
            onClose={() => setReportOpen(false)}
          />
        )}
      </div>
      {copied ? (
        <span className="ok">Copied</span>
      ) : (
        <button
          type="button"
          className="copy"
          title="Copy"
          aria-label="Copy response"
          onClick={handleCopy}
        >
          <FiCopy size={14} />
          <span>Copy</span>
        </button>
      )}
    </div>
  );
}

const ChatMessage = memo(function ChatMessage({
  message,
  threadId,
  messageIndex,
  onFeedback,
  onRetry,
}) {
  const isUser = message.role === MESSAGE_ROLES.USER;
  const showActions = !isUser && !message.isStreaming && !message.error && message.content;
  const showThinking = !isUser && message.isStreaming && !message.content;
  const showRetry = !isUser && message.error;

  let bubbleContent;
  if (isUser) {
    bubbleContent = message.content;
  } else if (showThinking) {
    bubbleContent = <ThinkingState />;
  } else if (message.isStreaming) {
    bubbleContent = (
      <span dangerouslySetInnerHTML={{ __html: formatChatMessageWithCaret(message.content) }} />
    );
  } else {
    bubbleContent = (
      <span dangerouslySetInnerHTML={{ __html: formatChatMessage(message.content) }} />
    );
  }

  return (
    <div className={`nova-row ${isUser ? "me" : "nova"}`}>
      {!isUser && <NovaMark size="sm" />}
      <div className="nova-msg-wrap">
        <div className="nova-bubble">{bubbleContent}</div>
        {showActions && (
          <MessageActions
            message={message}
            threadId={threadId}
            messageIndex={messageIndex}
            onFeedback={onFeedback}
          />
        )}
        {showRetry && (
          <div className="nova-retry-wrap">
            <button type="button" className="nova-retry-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

// --- Chat state hook (all existing logic preserved) ---

function useChatBotState(subscribed) {
  const { showToast } = useToast();
  const abortRef = useRef(null);
  const rafRef = useRef(null);
  const pendingContentRef = useRef("");

  const [messages, setMessages] = useState([]);
  const [examType, setExamType] = useState(EXAM_TYPES.ACADEMIC);
  const [threadId, setThreadId] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [pendingExamType, setPendingExamType] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [lastFailedQuestion, setLastFailedQuestion] = useState(null);
  const [chatsLeft, setChatsLeft] = useState(0);

  const hasMessages = messages.length > 0;

  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const result = await fetchChatHistory();
        if (cancelled) return;
        const historyMessages = Array.isArray(result?.messages) ? result.messages : [];
        const mapped = historyMessages
          .filter((message) => message && (message.role === MESSAGE_ROLES.USER || message.role === MESSAGE_ROLES.ASSISTANT) && typeof message.content === "string")
          .map((message) => ({ id: createMessageId(), role: message.role, content: message.content, timestamp: Date.now() }));
        if (mapped.length) {
          setMessages(mapped);
          if (result.exam_type === EXAM_TYPES.ACADEMIC) setExamType(EXAM_TYPES.ACADEMIC);
          else if (result.exam_type === EXAM_TYPES.CORE && CORE_NOVA_ACTIVE) setExamType(EXAM_TYPES.CORE);
        }
        setChatsLeft(typeof result?.chats_left === "number" ? result.chats_left : 0);

        // If history response has a thread_id, use it; otherwise start a new chat to get one
        if (result?.thread_id) {
          setThreadId(result.thread_id);
        } else {
          try {
            const currentExamType = result?.exam_type || EXAM_TYPES.ACADEMIC;
            const newChatResult = await startNewChat(currentExamType);
            if (!cancelled && newChatResult?.thread_id) {
              setThreadId(newChatResult.thread_id);
            }
          } catch { /* Soft-fail: threadId remains null until next interaction */ }
        }
      } catch { /* Soft-fail */ }
      finally { if (!cancelled) setIsLoadingHistory(false); }
    };
    loadHistory();
    return () => { cancelled = true; };
  }, []);

  const cancelStream = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    pendingContentRef.current = "";
  }, []);

  const updateMessage = useCallback((messageId, patch) => {
    setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, ...patch } : m));
  }, []);

  const updateMessageFeedback = useCallback((messageId, feedbackPatch) => {
    setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, feedback: { ...m.feedback, ...feedbackPatch } } : m));
  }, []);

  const streamReply = useCallback(
    async ({ question, examType: currentExamType, threadId: currentThreadId, onTokenUpdate, onComplete, onError, onChatsLeft }) => {
      cancelStream();
      const controller = new AbortController();
      abortRef.current = controller;
      pendingContentRef.current = "";

      const flushPending = () => { rafRef.current = null; onTokenUpdate(pendingContentRef.current); };
      const scheduleFlush = () => { if (rafRef.current) return; rafRef.current = requestAnimationFrame(flushPending); };
      const appendToken = (token) => { if (!token) return; pendingContentRef.current = `${pendingContentRef.current}${token}`; scheduleFlush(); };
      const userType = subscribed ? 'paid' : 'free';

      try {
        await streamChatReply({
          question, examType: currentExamType, threadId: currentThreadId, signal: controller.signal,
          onToken: appendToken, onError: appendToken,
          onChatsLeft,
          onDone: () => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; } onTokenUpdate(pendingContentRef.current); },
          userType : userType,
        });
        if (controller.signal.aborted) return;
        onComplete({ content: pendingContentRef.current, aborted: false });
      } catch (error) {
        if (error?.name === "AbortError") { onComplete({ content: pendingContentRef.current, aborted: true }); return; }
        onError(error);
      } finally { abortRef.current = null; pendingContentRef.current = ""; }
    }, [cancelStream]
  );

  const runStream = useCallback(
    async (question, assistantMessageId) => {
      setIsBusy(true);
      setIsStreaming(true);
      setLastFailedQuestion(null);
      await streamReply({
        question, examType, threadId,
        onChatsLeft: (val) => { setChatsLeft(typeof val === "number" ? val : 0); },
        onTokenUpdate: (content) => { updateMessage(assistantMessageId, { content }); },
        onComplete: ({ content, aborted }) => {
          setIsStreaming(false); setIsBusy(false);
          if (aborted) { updateMessage(assistantMessageId, { content: content || "", isStreaming: false }); return; }
          updateMessage(assistantMessageId, { content: content || "I couldn't generate a response. Please try again.", isStreaming: false, error: !content });
        },
        onError: (error) => {
          setIsStreaming(false); setIsBusy(false); setLastFailedQuestion(question);
          const errorMessage = error?.message || DEFAULT_ERROR_MESSAGE;
          updateMessage(assistantMessageId, { content: errorMessage, isStreaming: false, error: true });
          showToast({ type: 2, message: errorMessage });
        },
      });
    }, [examType, threadId, showToast, streamReply, updateMessage]
  );

  const sendMessage = useCallback(
    async (text) => {
      // if (!subscribed) return;
      const question = String(text || "").trim();
      if (!question || isBusy || isStreaming) return;
      const userMessage = { id: createMessageId(), role: MESSAGE_ROLES.USER, content: question, timestamp: Date.now() };
      const assistantMessage = { id: createMessageId(), role: MESSAGE_ROLES.ASSISTANT, content: "", timestamp: Date.now(), isStreaming: true };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      await runStream(question, assistantMessage.id);
    }, [isBusy, isStreaming, runStream, subscribed]
  );

  const retryLastMessage = useCallback(async () => {
    if (!lastFailedQuestion || isBusy || isStreaming) return;
    const assistantMessage = { id: createMessageId(), role: MESSAGE_ROLES.ASSISTANT, content: "", timestamp: Date.now(), isStreaming: true };
    setMessages((prev) => { const withoutErrors = prev.filter((m) => !m.error); return [...withoutErrors, assistantMessage]; });
    await runStream(lastFailedQuestion, assistantMessage.id);
  }, [isBusy, isStreaming, lastFailedQuestion, runStream]);

  const resetConversation = useCallback(() => {
    cancelStream(); setMessages([]); setThreadId(null); setIsBusy(false); setIsStreaming(false); setLastFailedQuestion(null);
  }, [cancelStream]);

  const confirmNewChat = useCallback(
    async ({ rating = null, comment = "" } = {}) => {
      if (isClearing) return;
      cancelStream(); setIsClearing(true);
      try {
        const trimmedComment = String(comment || "").trim();
        const hasFeedback = rating != null || trimmedComment.length > 0;
        if (hasFeedback) {
          if (!threadId) throw new Error("Unable to submit feedback. Please try again later.");
          await submitChatFeedback({ threadId, rating: rating == null ? null : rating, comment: trimmedComment });
        }
        const result = await startNewChat(examType);
        showToast({ type: 1, message: "New chat started." });
        setShowNewChatModal(false); resetConversation();
        if (result?.thread_id) setThreadId(result.thread_id);
      } catch (error) {
        showToast({ type: 2, message: error?.message || "Failed to start a new chat." });
      } finally { setIsClearing(false); }
    }, [cancelStream, examType, isClearing, resetConversation, showToast, threadId]
  );

  const requestNewChat = useCallback(() => {
    console.log(subscribed,isClearing, isLoadingHistory)
    if (isClearing || isLoadingHistory) return;
    if (hasMessages) { setShowNewChatModal(true); return; }
    confirmNewChat();
  }, [confirmNewChat, hasMessages, isClearing, isLoadingHistory, subscribed]);

  const cancelNewChat = useCallback(() => { setShowNewChatModal(false); }, []);

  const applyExamSwitch = useCallback(
    async (nextExamType, { rating = null, comment = "" } = {}) => {
      if (nextExamType === examType) return;
      cancelStream(); setIsClearing(true);
      try {
        const trimmedComment = String(comment || "").trim();
        const hasFeedback = rating != null || trimmedComment.length > 0;
        if (hasFeedback) {
          if (!threadId) throw new Error("Unable to submit feedback. Please try again later.");
          await submitChatFeedback({ threadId, rating: rating == null ? null : rating, comment: trimmedComment });
        }
        try { await clearChatHistory(examType); } catch (clearError) {
          showToast({ type: 2, message: clearError?.message || "Failed to clear previous chat history." });
        }
        setExamType(nextExamType); resetConversation(); setPendingExamType(null);
        const result = await startNewChat(nextExamType);
        if (result?.thread_id) setThreadId(result.thread_id);
        showToast({ type: 1, message: `Switched to ${EXAM_CONFIG[nextExamType]?.name || nextExamType}.` });
      } catch (error) {
        showToast({ type: 2, message: error?.message || "Failed to switch exam type." });
      } finally { setIsClearing(false); }
    }, [cancelStream, examType, resetConversation, showToast, threadId]
  );

  const requestExamSwitch = useCallback(
    (nextExamType) => {
      if (nextExamType === examType) return;
      if (nextExamType === EXAM_TYPES.CORE && !CORE_NOVA_ACTIVE) return;
      if (!hasMessages) { setExamType(nextExamType); return; }
      setPendingExamType(nextExamType);
    }, [examType, hasMessages]
  );

  const cancelExamSwitch = useCallback(() => { setPendingExamType(null); }, []);

  return {
    messages, examType, threadId, isBusy, isStreaming, isClearing, isLoadingHistory,
    pendingExamType, showNewChatModal, chatsLeft, sendMessage, retryLastMessage, requestNewChat,
    confirmNewChat, cancelNewChat, requestExamSwitch, applyExamSwitch, cancelExamSwitch,
    updateMessageFeedback, cancelStream,
  };
}

const ChatBotPanel = forwardRef(function ChatBotPanel(
  { subscribed, onClose },
  ref
) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const threadRef = useRef(null);
  const textareaRef = useRef(null);
  const newChatTextareaRef = useRef(null);
  console.log(subscribed)
  const [showCreditsPopover, setShowCreditsPopover] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const firstName = user?.first_name || "there";

  const {
    messages, examType, threadId, isBusy, isStreaming, isClearing, isLoadingHistory,
    pendingExamType, showNewChatModal, chatsLeft, sendMessage, retryLastMessage, requestNewChat,
    confirmNewChat, cancelNewChat, requestExamSwitch, applyExamSwitch, cancelExamSwitch,
    updateMessageFeedback, cancelStream,
  } = useChatBotState(subscribed);

  const credits = { remaining: chatsLeft, allowance:  subscribed ? 25 : 5 , plan: "gold" };

  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(null);
  const [feedbackError, setFeedbackError] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  useImperativeHandle(ref, () => ({ cancelStream }), [cancelStream]);

  const composerDisabled = isBusy || isStreaming || isClearing || isLoadingHistory;
  const exam = EXAM_CONFIG[examType];
  const streamingContent = messages.find((m) => m.isStreaming)?.content || "";
  const isLowBalance = credits.remaining <= 5 && subscribed;

  // Scroll to bottom on new messages
  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    thread.scrollTo({ top: thread.scrollHeight, behavior: "smooth" });
  }, [messages.length, streamingContent]);

  useEffect(() => {
    if (subscribed) textareaRef.current?.focus();
  }, [subscribed]);



  // Feedback modal state
  const showFeedbackModal = showNewChatModal || Boolean(pendingExamType);
  const feedbackMode = showNewChatModal ? "newChat" : pendingExamType ? "examSwitch" : null;

  useEffect(() => {
    if (showFeedbackModal) {
      setFeedbackComment(""); setFeedbackRating(null); setFeedbackHoverRating(null);
      setFeedbackError(""); setIsSubmittingFeedback(false);
      newChatTextareaRef.current?.focus();
    }
  }, [showFeedbackModal]);

  const autosize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, []);

  const handleSend = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const value = textarea.value.trim();
    if (!value || composerDisabled) return;
    sendMessage(value);
    textarea.value = "";
    textarea.style.height = "auto";
  }, [composerDisabled, sendMessage]);

  const handleComposerKeyDown = useCallback(
    (event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); handleSend(); } },
    [handleSend]
  );

  const handleFeedbackCancel = useCallback(() => {
    if (isClearing || isSubmittingFeedback) return;
    if (feedbackMode === "newChat") cancelNewChat();
    else cancelExamSwitch();
  }, [cancelExamSwitch, cancelNewChat, feedbackMode, isClearing, isSubmittingFeedback]);

  const handleFeedbackSubmit = useCallback(async () => {
    if (isClearing || isSubmittingFeedback) return;
    const payload = { rating: feedbackRating, comment: feedbackComment.trim() };
    setFeedbackError(""); setIsSubmittingFeedback(true);
    try {
      if (feedbackMode === "newChat") await confirmNewChat(payload);
      else if (feedbackMode === "examSwitch" && pendingExamType) await applyExamSwitch(pendingExamType, payload);
    } finally { setIsSubmittingFeedback(false); }
  }, [applyExamSwitch, confirmNewChat, feedbackComment, feedbackMode, feedbackRating, isClearing, isSubmittingFeedback, pendingExamType]);

  const handleSkipAndStart = useCallback(async () => {
    if (isClearing || isSubmittingFeedback) return;
    setIsSubmittingFeedback(true);
    try {
      if (feedbackMode === "newChat") await confirmNewChat({});
      else if (feedbackMode === "examSwitch" && pendingExamType) await applyExamSwitch(pendingExamType, {});
    } finally { setIsSubmittingFeedback(false); }
  }, [applyExamSwitch, confirmNewChat, feedbackMode, isClearing, isSubmittingFeedback, pendingExamType]);

  const pendingExamName = EXAM_CONFIG[pendingExamType]?.name || pendingExamType;
  const feedbackBusy = isClearing || isSubmittingFeedback;
  const feedbackTitle = feedbackMode === "examSwitch" ? `Switch to ${pendingExamName}?` : "Start a new chat?";
  const feedbackPrimaryLabel = feedbackMode === "examSwitch"
    ? (feedbackBusy ? "Switching..." : "Switch & start fresh")
    : (feedbackBusy ? "Starting..." : "Start new chat");

  return (
    <div className="nova-panel" role="dialog" aria-label="Nova AI chat">
      <div className="nova-chat">
        {/* === HEADER === */}
        <div className="nova-chat-head">
          <div className="nova-brand">
            <NovaMark />
            <div className="nm">
              <b>Nova <span className="nova-dot" title="Online" /></b>
              <small>Your AI PTE Coach</small>
            </div>
          </div>
          <div className="nova-head-right">
            {!isLoadingHistory && 
              <button
                type="button"
                className={`nova-credit-pill ${isLowBalance ? "amber" : ""}`}
                title="Nova Credits"
                onClick={() => setShowCreditsPopover((v) => !v)}
              >
                <svg viewBox="0 0 24 24"><path d={NOVA_STAR_PATH} /></svg>
                <span>{credits.remaining}</span>
              </button>
            }
            {messages.length > 0 && 
              <button
                type="button"
                className="nova-newchat-btn"
                title="New chat"
                aria-label="Start new chat"
                disabled={isClearing || isLoadingHistory}
                onClick={requestNewChat}
              >
                <FiPlus size={16} />
              </button>
            }
            <button
              type="button"
              className="nova-close"
              aria-label="Close chat"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        {/* === AUTHORITY STRIP === */}
        <div className="nova-authority">
          <img src={varunImg} alt="Varun Dhawan" className="nova-authority-avatar" />
          <span className="nova-authority-text">
            Trained on <strong>Varun Dhawan's</strong> method · 14× Perfect 90
          </span>
          <svg className="nova-authority-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>

        {/* === EXAM TYPE ROW === */}
        <div className="nova-exam-row">
          <div className="nova-seg" role="tablist" aria-label="Exam type">
            <button type="button" role="tab" aria-selected={examType === EXAM_TYPES.ACADEMIC} className={examType === EXAM_TYPES.ACADEMIC ? "on" : ""} disabled={composerDisabled} onClick={() => requestExamSwitch(EXAM_TYPES.ACADEMIC)}>Academic</button>
            <button type="button" role="tab" aria-selected={examType === EXAM_TYPES.CORE} className={examType === EXAM_TYPES.CORE ? "on" : ""} disabled={composerDisabled || !CORE_NOVA_ACTIVE} title={CORE_NOVA_ACTIVE ? undefined : "PTE Core is not available yet"} onClick={() => requestExamSwitch(EXAM_TYPES.CORE)}>Core</button>
          </div>
          <span className="nova-exam-helper">
            You're on <strong>{exam.name}</strong> — tap {examType === EXAM_TYPES.ACADEMIC ? "Core" : "Academic"} to switch anytime
          </span>
        </div>

        {/* === REMAINING MESSAGES (hardcoded) === */}
        <div className="nova-remaining d-none">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d={NOVA_STAR_PATH} />
          </svg>
          <span><strong>25</strong> of 25 questions today · refills 12:00 am AEST</span>
        </div>

        {/* === LOW BALANCE STRIP (shown when ≤5 credits) === */}
        {(isLowBalance && !isLoadingHistory) && (
          <div className="nova-low-strip">
            <svg className="nova-low-strip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <div className="nova-low-strip-text">
              <strong>{credits.remaining} credits left today</strong>
              {/* <span>All 25 come back in <b>6h 12m</b></span> */}
            </div>
            {/* <button type="button" className="nova-topup-btn">Top up</button> */}
          </div>
        )}

        {/* === THREAD BODY === */}
        <div className="nova-thread" ref={threadRef} role="log" aria-live="polite" aria-relevant="additions text">

          {/* Loading state */}
          {isLoadingHistory && !messages.length && (
            <div className="nova-history-loading" role="status" aria-live="polite">
              <div className="nova-history-loading-ava"><NovaMark size="lg" /></div>
              <h2>Loading your chat…</h2>
              <p>Nova is restoring your previous conversation.</p>
              <div className="nova-history-loading-bar" aria-hidden="true"><span /></div>
            </div>
          )}

          {/* Arrival moment */}
          {!isLoadingHistory && !messages.length && (
            <div className="nova-arrival">
              <div className="nova-arrival-blob" aria-hidden="true" />
              <div className="nova-arrival-greeting text-center">
                <div className="nova-mark-wrapper">
                  <NovaMark size="lg" />
                </div>
                <h3>Hi {firstName}, I'm Nova.</h3>
                <p>I'm <strong>Varun's coaching brain, cloned</strong> — every answer follows the method behind his 14 Perfect 90s.</p>
              </div>

              {/* Founder card */}
             <div className="nova-founder-card">
                <img
                  src={varunImg}
                  alt="Varun Dhawan"
                  className="nova-founder-photo"
                />

                <div className="nova-founder-info">
                  <div>
                    <h4>Varun Dhawan</h4>
                    <p>
                      Founder, Language Academy / 14× Perfect 90 · 50,000+ successful students
                    </p>
                  </div>

                  <div className="nova-founder-sig">
                    <span>Varun</span>
                    <div className="nova-founder-sig-line" />
                  </div>
                </div>
              </div>

              {/* Credits row */}
              <div className="nova-credits-row">
                <svg viewBox="0 0 24 24"><path d={NOVA_STAR_PATH} /></svg>
                <div className="nova-credits-row-text">
                  {/* TODO: Replace with real credits from API */}
                  <strong>{credits.remaining} Nova Credits, { subscribed ? 'today' : 'one time only'}</strong>
                  <small>1 credit = 1 question { subscribed && '· refills 12:00 AM AEST'}</small>
                </div>
              </div>

              {/* Starter prompts */}
              <div className="nova-starters">
                <div className="nova-starters-eyebrow">START HERE</div>
                <div className="nova-starters-list">
                  {STARTER_QUESTIONS.map((question, index) => (
                    <button
                      key={question}
                      type="button"
                      className="nova-starter-card"
                      disabled={composerDisabled}
                      onClick={() => sendMessage(question)}
                    >
                      <span className="nova-starter-num">{index + 1}</span>
                      <span className="nova-starter-label">{question}</span>
                      <svg className="nova-starter-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 17 17 7M7 7h10v10" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message}
              threadId={threadId}
              messageIndex={index}
              onFeedback={(feedback) => updateMessageFeedback(message.id, feedback)}
              onRetry={retryLastMessage}
            />
          ))}
        </div>

        {/* === COMPOSER === */}
        {/* {!subscribed ? (
          <div className="nova-composer nova-composer-subscribe">
            <button type="button" className="nova-subscribe-btn" onClick={() => navigate(SUBSCRIPTION_ROUTE)}>
              Subscribe Now
            </button>
            <div className="nova-disc">
              Nova is a Premium feature. Subscribe to unlock your 24/7 AI PTE coach.
            </div>
          </div>
        ) : ( */}
          <div className="nova-composer">
            <div className="nova-inputwrap">
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder="Ask Nova anything about your PTE exam…"
                aria-label="Message Nova"
                disabled={composerDisabled}
                onInput={autosize}
                onKeyDown={handleComposerKeyDown}
              />
              {/* {isStreaming ? (
                <button
                  type="button"
                  className="nova-stop-btn"
                  title="Stop"
                  aria-label="Stop generating"
                  onClick={cancelStream}
                >
                  <FiSquare size={14} />
                </button>
              ) : ( */}
                <button
                  type="button"
                  className="nova-send"
                  title="Send"
                  aria-label="Send message"
                  disabled={composerDisabled || isStreaming}
                  onClick={handleSend}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10l11 2-11 2z" />
                  </svg>
                </button>
              {/* )} */}
            </div>
            <div className="nova-disc">
              Nova follows Varun's method but can occasionally be wrong — check anything important with your LA coach.
            </div>
          </div>
        {/* )} */}

        {/* === NEW CHAT / EXAM SWITCH FEEDBACK MODAL === */}
        {showFeedbackModal && (
          <div className="nova-modal show" role="dialog" aria-modal="true" aria-labelledby="nova-feedback-title">
            <div className="nova-mcard">
              <div className="nova-mcard-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h3 id="nova-feedback-title">{feedbackTitle}</h3>
              <p>This conversation stays in your history — Nova just starts fresh. No credits are used.</p>

              <div className="nova-star-rating" role="radiogroup" aria-label="Conversation rating">
                <div>
                  <div className="nova-star-rating-label">How helpful was Nova? <em>Optional</em></div>
                  <div style={{ display: "flex", gap: "4px" }}>
                    {[1, 2, 3, 4, 5].map((star) => {
                      const activeValue = feedbackHoverRating ?? feedbackRating ?? 0;
                      const isFilled = star <= activeValue;
                      return (
                        <button
                          key={star}
                          type="button"
                          className={`nova-star ${isFilled ? "filled" : ""}`}
                          role="radio"
                          aria-checked={feedbackRating === star}
                          aria-label={`${star} star${star === 1 ? "" : "s"}`}
                          disabled={feedbackBusy}
                          onMouseEnter={() => setFeedbackHoverRating(star)}
                          onMouseLeave={() => setFeedbackHoverRating(null)}
                          onClick={() => setFeedbackRating((c) => c === star ? null : star)}
                        >
                          <FiStar size={22} aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <textarea
                ref={newChatTextareaRef}
                rows={2}
                value={feedbackComment}
                placeholder="What could Nova have explained better?"
                aria-label="Conversation feedback"
                disabled={feedbackBusy}
                onChange={(event) => { setFeedbackComment(event.target.value); if (feedbackError) setFeedbackError(""); }}
              />
              {feedbackError && <p className="nova-new-chat-error">{feedbackError}</p>}
              <div className="nova-mact">
                <button type="button" className="cancel" onClick={handleFeedbackCancel} disabled={feedbackBusy}>Cancel</button>
                <button type="button" className="go" onClick={handleFeedbackSubmit} disabled={feedbackBusy}>{feedbackPrimaryLabel}</button>
              </div>
              <button type="button" className="nova-skip-link" onClick={handleSkipAndStart} disabled={feedbackBusy}>
                Skip rating and start fresh
              </button>
            </div>
          </div>
        )}

        {/* === CREDITS POPOVER === */}
        {showCreditsPopover && (
          <div className="nova-credits-popover">
            <div className="nova-credits-popover-head">
              <div className="nova-credits-popover-icon">
                <svg viewBox="0 0 24 24"><path d={NOVA_STAR_PATH} /></svg>
              </div>
              <div className="nova-credits-popover-title">
                <h4>Nova Credits</h4>
                {/* TODO: Replace plan label with real data */}
                { subscribed ? 
                <small>Resets daily</small>
                :<small>Limited credits</small>
                }
              </div>
              <button type="button" className="nova-close" onClick={() => setShowCreditsPopover(false)} aria-label="Close credits">×</button>
            </div>
            <div className="nova-credits-balance">
              <span className="nova-credits-balance-num">{credits.remaining}</span>
              <span className="nova-credits-balance-label">of {credits.allowance} left {subscribed && "today"}</span>
            </div>
            <div className="nova-credits-track">
              <div
                className={`nova-credits-track-fill ${isLowBalance ? "amber" : ""}`}
                style={{ width: `${(credits.remaining / credits.allowance) * 100}%` }}
              />
            </div>
            {/* TODO: Replace with real countdown from server clock */}
            {subscribed && 
              <div className="nova-credits-refill">Refills to 25 · 12:00 AM AEST</div>
            }
            <ul className="nova-credits-rules">
              <li>
                <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                1 credit = 1 question to Nova
              </li>
              {subscribed ? 
                <li>
                  <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                  Refills to 25 every day at midnight AEST
                </li> : <li>
                  <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                  5 credits total, one time only
                </li>
              }
              {!subscribed && 
              <li> <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                No daily refill</li>}
              <li>
                <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                Unused credits don't roll over
              </li>
            </ul>
            {/* <button type="button" className="nova-topup-btn" style={{ width: "100%", padding: "14px", borderRadius: "14px", fontSize: "14px" }}>
              Top up 25 credits — AUD $9
            </button>
            <div className="nova-disc" style={{ marginTop: "8px" }}>One-off. Never expires.</div> */}
          </div>
        )}

        {/* === HISTORY PANEL === */}
        {showHistory && (
          <div className="nova-history-panel">
            <div className="nova-history-head">
              <NovaMark />
              <div className="nova-history-head-text">
                <h4>Your chats with Nova</h4>
                <small>Kept 90 days</small>
              </div>
              <button type="button" className="nova-history-close" onClick={() => setShowHistory(false)} aria-label="Close history">×</button>
            </div>
            <div className="nova-history-list">
              {/* TODO: Replace with real history from GET /nova/threads */}
              <div className="nova-history-item">
                <div className="nova-history-item-icon speaking">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /></svg>
                </div>
                <div className="nova-history-item-content">
                  <div className="nova-history-item-title">Why am I stuck at 65 in Speaking?</div>
                  <div className="nova-history-item-meta">Today · 4 questions · 4 credits</div>
                </div>
                <FiChevronRight className="nova-history-item-chevron" size={16} />
              </div>
              <div className="nova-history-item">
                <div className="nova-history-item-icon writing">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /></svg>
                </div>
                <div className="nova-history-item-content">
                  <div className="nova-history-item-title">How does the algorithm mark my essay?</div>
                  <div className="nova-history-item-meta">Yesterday · 3 questions · 3 credits</div>
                </div>
                <FiChevronRight className="nova-history-item-chevron" size={16} />
              </div>
              <div className="nova-history-item">
                <div className="nova-history-item-icon listening">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></svg>
                </div>
                <div className="nova-history-item-content">
                  <div className="nova-history-item-title">What should I practise in my last 12 days?</div>
                  <div className="nova-history-item-meta">2 days ago · 6 questions · 6 credits</div>
                </div>
                <FiChevronRight className="nova-history-item-chevron" size={16} />
              </div>
            </div>
            <div className="nova-history-footer">
              <span className="nova-history-footer-stat">13 credits spent this week</span>
              <button type="button" className="nova-history-footer-link">View all →</button>
            </div>
          </div>
        )}

        {/* === UPGRADE MODAL (out of credits — free user) === */}
        {showUpgradeModal && (
          <div className="nova-upgrade-modal" role="dialog" aria-modal="true" aria-labelledby="nova-upgrade-title">
            <div className="nova-upgrade-card">
              {/* Left column */}
              <div className="nova-upgrade-left">
                <div className="nova-upgrade-left-blob" aria-hidden="true" />
                <div className="nova-upgrade-left-content">
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                    <NovaMark />
                    <span style={{ fontSize: "17px", fontWeight: 700, color: "#fff" }}>Nova</span>
                  </div>
                  <h2 id="nova-upgrade-title">You've used all 10 free credits.</h2>
                  <div className="serif-line">Gold students get 25. Every single day.</div>

                  <div className="nova-upgrade-quote">
                    <img src={varunImg} alt="Varun Dhawan" className="nova-upgrade-quote-avatar" />
                    <div className="nova-upgrade-quote-text">
                      {/* TODO: Confirm quote wording before ship */}
                      <p>"I put fourteen Perfect 90s into this thing. Use it properly."</p>
                      <small>Varun Dhawan · Founder</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="nova-upgrade-right">
                <button type="button" className="nova-upgrade-right-close" onClick={() => setShowUpgradeModal(false)} aria-label="Close">×</button>
                <div className="nova-upgrade-eyebrow">NOVA CREDITS</div>
                <h3>Keep asking. Keep improving.</h3>

                {/* Free card */}
                <div className="nova-plan-card">
                  <h4>0 credits left</h4>
                  <ul className="nova-plan-card-features">
                    <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>10 credits total, one time only</li>
                    <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>No daily refill</li>
                    <li><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>PTE Academic only</li>
                  </ul>
                  <span className="disabled-chip">Current plan</span>
                </div>

                {/* Gold card */}
                <div className="nova-plan-card gold">
                  <span className="recommended-badge">RECOMMENDED</span>
                  <h4>25 credits every day</h4>
                  <ul className="nova-plan-card-features">
                    <li><svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>Midnight refill — never runs out</li>
                    <li><svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>PTE Academic + Core</li>
                    <li><svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>Secret Strategy Videos + mocks</li>
                  </ul>
                  <button type="button" className="upgrade-cta" onClick={() => { setShowUpgradeModal(false); navigate(SUBSCRIPTION_ROUTE); }}>
                    Upgrade to Gold
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </button>
                </div>

                {/* Trust row */}
                <div className="nova-trust-row">
                  <span className="nova-trust-row-score">4.8</span>
                  <div className="nova-trust-row-stars">
                    {[1,2,3,4,5].map((s) => <svg key={s} viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>)}
                  </div>
                  <span className="nova-trust-row-text">2,624 ratings · 50,000+ successful students · 11+ locations</span>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
      <span className="nova-panel-context" aria-hidden="true">{exam.name}</span>
    </div>
  );
});

export default ChatBotPanel;
